import React from 'react';
import { Target, Eye, Award, Shield, Heart, Zap, User, Flag, Briefcase, Book } from 'lucide-react';

const About = () => {
    return (
        <div className="pt-20 pb-20 bg-gray-50 font-sans text-gray-800">
            {/* Header */}
            <div className="bg-primary text-white py-32 text-center mb-0 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                <div className="container relative z-10">
                    <span className="text-secondary font-bold tracking-widest uppercase mb-4 block">Our Identity</span>
                    <h1 className="text-5xl md:text-7xl font-black mb-6">About TCM Matric School</h1>
                    <p className="max-w-3xl mx-auto text-blue-100 text-xl font-light leading-relaxed">
                        Established in 1995, we are a premier educational institution in Erode, committed to sculpting the leaders of tomorrow through holistic education, ethical grounding, and academic rigor.
                    </p>
                </div>
            </div>

            {/* Introduction Quote */}
            <div className="container -mt-10 relative z-20 mb-20 text-center">
                <div className="bg-white p-10 shadow-2xl rounded-2xl max-w-4xl mx-auto border-t-8 border-secondary">
                    <p className="text-2xl font-serif italic text-gray-700">
                        "To educate a man in mind and not in morals is to educate a menace to society."
                    </p>
                    <p className="mt-4 font-bold text-primary uppercase tracking-wider">— Theodore Roosevelt</p>
                </div>
            </div>

            {/* Mission & Vision - Detailed */}
            <div className="container py-12">
                <div className="grid md:grid-cols-2 gap-12">
                    <div className="bg-white p-12 shadow-xl rounded-3xl border border-gray-100 flex flex-col items-center text-center">
                        <div className="w-24 h-24 bg-secondary/10 rounded-full flex items-center justify-center mb-8 text-secondary">
                            <Target size={48} />
                        </div>
                        <h3 className="text-3xl font-black text-primary mb-6">Our Mission</h3>
                        <p className="text-gray-600 leading-relaxed text-lg mb-8">
                            To create a transformative educational experience that empowers students to achieve academic excellence, develop strength of character, and contribute positively to the global community. We are dedicated to providing a safe, inclusive, and technologically advanced environment where every child feels valued, challenged, and supported.
                        </p>
                        <ul className="text-left space-y-3 w-full max-w-md mx-auto">
                            <li className="flex items-center gap-3"><span className="w-3 h-3 bg-secondary rounded-full"></span> Foster critical thinking.</li>
                            <li className="flex items-center gap-3"><span className="w-3 h-3 bg-secondary rounded-full"></span> Encourage creativity.</li>
                            <li className="flex items-center gap-3"><span className="w-3 h-3 bg-secondary rounded-full"></span> Build moral integrity.</li>
                        </ul>
                    </div>
                    <div className="bg-primary p-12 shadow-xl rounded-3xl flex flex-col items-center text-center text-white relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
                        <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center mb-8 text-secondary relative z-10">
                            <Eye size={48} />
                        </div>
                        <h3 className="text-3xl font-black text-white mb-6 relative z-10">Our Vision</h3>
                        <p className="text-blue-100 leading-relaxed text-lg mb-8 relative z-10">
                            To be a beacon of refined knowledge and a hub of innovation, recognized globally for producing ethical leaders who drive positive change. We envision a future where TCM alumni are at the forefront of every field, upholding values of compassion, sustainability, and equity.
                        </p>
                        <ul className="text-left space-y-3 w-full max-w-md mx-auto relative z-10">
                            <li className="flex items-center gap-3"><span className="w-3 h-3 bg-secondary rounded-full"></span> Global recognition.</li>
                            <li className="flex items-center gap-3"><span className="w-3 h-3 bg-secondary rounded-full"></span> Continuous innovation.</li>
                            <li className="flex items-center gap-3"><span className="w-3 h-3 bg-secondary rounded-full"></span> Ethical leadership.</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Detailed History Timeline */}
            <div className="py-24 container">
                <div className="text-center mb-16">
                    <span className="text-secondary font-bold uppercase tracking-widest">Our Journey</span>
                    <h2 className="text-4xl md:text-5xl font-black text-primary mt-2">Milestones of Excellence</h2>
                </div>

                <div className="relative border-l-4 border-gray-200 ml-6 md:ml-1/2 space-y-16">
                    {[
                        { year: '1995', title: 'The Genesis', desc: 'TCM Matriculation School was founded by Shri. K.N. Palanisamy with a vision to bring quality English education to Erode. Started with 5 classrooms and 80 students.' },
                        { year: '1998', title: 'First Infrastructure Expansion', desc: 'Inaugurated the main "Heritage Block" with 15 classrooms and a library. The school strength crossed 500 students.' },
                        { year: '2005', title: 'Matriculation Success', desc: 'The first batch of 10th-grade students appeared for Board Exams, achieving a historic 100% pass rate with 5 state ranks.' },
                        { year: '2010', title: 'Upgrade to Higher Secondary', desc: 'Recognized as a Higher Secondary School. Physics, Chemistry, and Biology labs were inaugurated by the District Educational Officer.' },
                        { year: '2015', title: 'Digital Revolution', desc: 'Partnered with EduComp to introduce Smart Classes in all rooms. Launched the first Computer Science lab with 50 systems.' },
                        { year: '2020', title: 'Silver Jubilee', desc: 'Celebrated 25 years of excellence. Launched the new "Horizon Sports Complex" with synthetic courts for Tennis and Basketball.' },
                        { year: '2024', title: 'Global Outlook', desc: 'Introduced Robotics and AI curriculum in partnership with IIT-Madras alumni. Student strength touched 1,500.' }
                    ].map((event, i) => (
                        <div key={i} className={`relative pl-12 md:pl-0 ${i % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12 md:ml-auto'} md:w-1/2`}>
                            <div className="absolute top-0 left-[-10px] md:left-auto md:right-[-10px] w-5 h-5 bg-secondary rounded-full border-4 border-white shadow-md z-10" style={i % 2 !== 0 ? { left: '-10px', right: 'auto' } : {}}></div>
                            <span className="text-5xl font-black text-gray-200 block mb-2">{event.year}</span>
                            <h3 className="text-2xl font-bold text-primary mb-2">{event.title}</h3>
                            <p className="text-gray-600 leading-relaxed">{event.desc}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* School Profile - Data Dense */}
            <div className="bg-white py-24 border-y border-gray-200">
                <div className="container">
                    <div className="grid lg:grid-cols-4 gap-8">
                        <div className="lg:col-span-1">
                            <h2 className="text-3xl font-black text-primary mb-6">School Profile</h2>
                            <p className="text-gray-500 mb-6">A quick snapshot of our institutional identity.</p>
                            <div className="p-6 bg-blue-50 rounded-xl">
                                <h4 className="font-bold text-primary mb-2">Affiliation</h4>
                                <p className="text-sm text-gray-600">Recognized by the Government of Tamil Nadu (Matriculation Board).</p>
                            </div>
                        </div>
                        <div className="lg:col-span-3">
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {[
                                    { label: 'School Name', value: 'T.C.M. Matriculation School' },
                                    { label: 'School Code', value: 'TCM-ERD-045' },
                                    { label: 'Founded In', value: '1995' },
                                    { label: 'Management', value: 'Private Unaided Trust' },
                                    { label: 'Trust Name', value: 'Ramachandra Educational Trust' },
                                    { label: 'Category', value: 'Co-Ed' },
                                    { label: 'Medium', value: 'English' },
                                    { label: 'Levels', value: 'KG - Grade 12' },
                                    { label: 'Academic Year', value: 'June to April' },
                                    { label: 'Total Area', value: '5.5 Acres' },
                                    { label: 'Built-up Area', value: '45,000 Sq. Ft.' },
                                    { label: 'District', value: 'Erode' },
                                ].map((item, i) => (
                                    <div key={i} className="flex flex-col border border-gray-100 p-4 rounded-lg hover:border-secondary transition-colors">
                                        <span className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">{item.label}</span>
                                        <span className="text-lg font-bold text-primary">{item.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            {/* Trust Section - Brief Overview */}
            <div className="bg-primary py-24 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="container relative z-10 text-center max-w-4xl mx-auto">
                    <span className="text-secondary font-bold uppercase tracking-[0.2em] mb-4 block">Our Foundation</span>
                    <h2 className="text-4xl md:text-6xl font-black mb-8 text-secondary drop-shadow-lg">Ramachandra Educational Trust</h2>
                    <p className="text-xl text-blue-100 leading-relaxed">
                        Established with a philanthropic vision, the <strong>Ramachandra Educational Trust</strong> is the backbone of TCM Matriculation School. Dedicated to uplifting society through quality education, the Trust has been instrumental in bridging the gap between rural potential and global opportunities for over three decades.
                    </p>
                </div>
            </div>

            {/* Leadership & Management */}
            <div className="py-24 container">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-black text-primary">The Guiding Lights</h2>
                    <p className="text-gray-500 mt-2">Our visionary management team.</p>
                </div>

                <div className="flex flex-wrap justify-center gap-20">
                    {[
                        { name: "PETHAN G", role: "Correspondent", img: "/correspondent_final.jpg" },
                        { name: "P MANIBHARATHI. BE, B. ED.", role: "PRINCIPAL", img: "/principal_new.jpg" }
                    ].map((leader, i) => (
                        <div key={i} className="group text-center">
                            <div className="w-56 h-56 mx-auto rounded-full overflow-hidden mb-6 border-4 border-gray-100 shadow-xl group-hover:border-secondary transition-all">
                                <img src={leader.img} alt={leader.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                            </div>
                            <h3 className="text-xl font-bold text-primary">{leader.name}</h3>
                            <p className="text-secondary font-bold text-sm tracking-widest uppercase mt-1">{leader.role}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Teacher's Section - Dynamic from Uploads */}
            <div className="py-24 bg-gray-50 border-t border-gray-200">
                <div className="container text-center">
                    <span className="text-secondary font-bold uppercase tracking-widest">Faculty</span>
                    <h2 className="text-4xl font-black text-primary mt-2 mb-16">Meet Our Dedicated Teachers</h2>

                    <div className="flex flex-wrap justify-center gap-12">
                        {[
                            {
                                img: "/teachers/teacher1.png",
                                name: "Mrs. Vinothini",
                                role: "B.A. English",
                                quote: "Language is the road map of a culture. It tells you where its people come from and where they are going."
                            },
                            {
                                img: "/teachers/teacher2.png",
                                name: "Mrs. Nathiya",
                                role: "B.Sc. Maths",
                                quote: "Mathematics is not just about numbers, equations, computations, or algorithms: it is about understanding."
                            },
                            {
                                img: "/teachers/teacher3_white.png",
                                name: "Mrs. Kowsiga",
                                role: "B.Sc., B.Ed.",
                                quote: "Education is the most powerful weapon which you can use to change the world."
                            },
                            {
                                img: "/teachers/teacher4.png",
                                name: "Mrs. Ramya",
                                role: "M.Com",
                                quote: "Commerce changes the fate and genius of nations."
                            },
                            {
                                img: "/teachers/teacher5_white.png",
                                name: "Mrs. Jeeva",
                                role: "M.Sc., B.Ed.",
                                quote: "Science is a way of thinking much more than it is a body of knowledge."
                            }
                        ].map((teacher, i) => (
                            <div key={i} className="flex flex-col items-center group w-72 bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300">
                                <div className="w-48 h-48 rounded-full border-4 border-gray-100 shadow-inner overflow-hidden mb-6 group-hover:scale-105 transition-transform duration-300 group-hover:border-secondary">
                                    <img src={teacher.img} alt={teacher.name} className="w-full h-full object-cover" />
                                </div>
                                <h4 className="font-bold text-primary text-xl mb-1">{teacher.name}</h4>
                                <span className="text-sm font-bold text-secondary uppercase tracking-widest mb-4">{teacher.role}</span>
                                <p className="text-gray-600 italic text-sm leading-relaxed">"{teacher.quote}"</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Core Values Grid */}
            <div className="bg-primary text-white py-24">
                <div className="container">
                    <h2 className="text-4xl font-black text-center mb-16">The Values We Live By</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            { icon: Heart, title: 'Empathy', desc: 'Understanding and sharing the feelings of others. We teach kindness before calculus.' },
                            { icon: Shield, title: 'Integrity', desc: 'Doing the right thing even when no one is watching. Honesty is our hallmark.' },
                            { icon: Zap, title: 'Innovation', desc: 'Constantly seeking new and better ways to solve problems. We embrace change.' },
                            { icon: Briefcase, title: 'Responsibility', desc: 'Being accountable for our actions and contributing to the community.' },
                            { icon: Award, title: 'Excellence', desc: 'Striving for the highest quality in every endeavor. Good enough is never enough.' },
                            { icon: Flag, title: 'Patriotism', desc: 'Love for the nation and a commitment to its progress.' },
                            { icon: Book, title: 'Curiosity', desc: 'A lifelong desire to learn and explore new ideas. Questions are encouraged.' },
                            { icon: User, title: 'Respect', desc: 'Treating everyone with dignity and valuing diversity in all forms.' }
                        ].map((val, idx) => (
                            <div key={idx} className="bg-white/5 p-8 rounded-2xl hover:bg-white/10 transition-colors border border-white/5">
                                <val.icon className="w-10 h-10 text-secondary mb-6" />
                                <h4 className="text-xl font-bold mb-3">{val.title}</h4>
                                <p className="text-gray-400 text-sm leading-relaxed">{val.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Policies Section */}
            <div className="py-24 container">
                <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="md:w-1/3">
                        <h3 className="text-3xl font-black text-primary mb-4">School Policies</h3>
                        <p className="text-gray-600">We maintain a disciplined environment through transparent policies.</p>
                    </div>
                    <div className="md:w-2/3 grid gap-4">
                        {[
                            { title: 'Anti-Bullying Policy', desc: 'Zero tolerance towards physical or verbal bullying. We ensure a safe space for every child.' },
                            { title: 'Child Protection Policy', desc: 'Comprehensive guidelines to ensure the safety and well-being of students on campus.' },
                            { title: 'Digital Usage Policy', desc: 'Strict rules regarding the use of mobile phones and internet within the school premises.' }
                        ].map((policy, i) => (
                            <div key={i} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                                <h4 className="font-bold text-primary text-lg mb-2">{policy.title}</h4>
                                <p className="text-sm text-gray-500">{policy.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

        </div>
    );
};
export default About;
